import { Fragment, useEffect, useMemo, useState } from "react";
import { Search, Filter, Download, X, Plus, Calendar, FilePlus2, ChevronDown, ChevronRight, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtDate, formatIDR } from "@/lib/constants";
import { computeExpectedNet, computeTotalReceived, getReceiptStatus } from "@/lib/paymentReceipts";
import api, { formatDetail } from "@/lib/api";
import OverReceivedBadge from "@/components/OverReceivedBadge";
import { useSaveWithRetry } from "@/lib/saveRetry";
import PaymentReceiptDialog from "@/components/PaymentReceiptDialog";
import IncomeLedgerDialog from "@/components/IncomeLedgerDialog";
import EditInvoiceDialog from "./EditInvoiceDialog";
import AuditLogButton from "@/components/AuditLogButton";

/**
 * Invoice & Piutang Tab — 149-cw-r123 (2026-08-30) R5 mockup parity.
 * Palette: navy/putih/abu-abu. Hijau=lunas, oranye=parsial, merah=terlambat.
 * Selected row: left-border navy + radio navy. "Catat Pembayaran" navy button
 * membuka PaymentReceiptDialog (form terpusat R4).
 */
export default function InvoicePiutangTab({ invoiceRows, loading, onRefresh, initialProjectId = "", projects = [] }) {
  const [q, setQ] = useState("");
  const [year, setYear] = useState("all");
  const [month, setMonth] = useState("all");
  // 2026-10-14 (user request): filter proyek dipecah — Induk Project & Sub Project.
  // Sub Project baru bisa dipilih setelah Induk dipilih, supaya daftar tidak lagi
  // penuh nama induk berulang untuk tiap WO/VO.
  const [parentId, setParentId] = useState("all");
  const [subId, setSubId] = useState("all");
  const [client, setClient] = useState("all");
  const [status, setStatus] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  // 16-CONSISTENT-BADGE-R1 (2026-06-14, user request): badge klasifikasi selisih
  // per baris invoice — sumber data sama (/overreceived/flagged) dgn Dashboard,
  // Collection, dan tab Verifikasi Penerimaan.
  const [overMap, setOverMap] = useState({});
  useEffect(() => {
    api.get("/overreceived/flagged")
      .then(({ data }) => {
        const m = {};
        for (const it of data?.items || []) m[it.project_id] = it;
        setOverMap(m);
      })
      .catch(() => {});
  }, []);
  const onOverClassified = (pid, doc) => setOverMap((m) => ({ ...m, [pid]: { ...(m[pid] || {}), classification: doc } }));
  // 2026-06-11 (user request): pengelompokan invoice per induk project + chevron
  // breakdown (konsisten dgn tab Ringkasan). Key = head project id.
  const [expandedGroups, setExpandedGroups] = useState(() => new Set());
  const toggleGroup = (id) => setExpandedGroups((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  // 149-cw-r138: Auto-buka filter panel kalau ada preset filter proyek dari URL,
  // supaya user langsung lihat filter aktif yang membuatnya berbeda dari tampilan default.
  const [showFilter, setShowFilter] = useState(!!initialProjectId);
  // 2026-09-24 (user request R99): konteks dialog Catat Pembayaran — dibuka
  // langsung dari tombol di kanan daftar invoice (panel Detail Invoice dihapus).
  const [receiptCtx, setReceiptCtx] = useState(null); // { project, entry|null }
  const [editCtx, setEditCtx] = useState(null); // 2026-09-24: { project, entry } utk dialog Edit Invoice
  // 149-cw-r140 (2026-08-31): Dialog "Buat Invoice" — buka IncomeLedgerDialog dgn
  // proyek yang di-scoped ATAU dipilih via picker. Solve issue user: WO yang belum
  // punya invoice tidak muncul di filter dropdown karena dropdown dulu di-derive dari
  // invoice rows saja. Sekarang dropdown pakai `projects` (semua project user visible).
  const [createInvoiceDlg, setCreateInvoiceDlg] = useState({ open: false, project: null });
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQ, setPickerQ] = useState("");

  // Non-retention-claim entries only (klaim tampil di tab Retensi & Collection).
  const baseRows = useMemo(() => invoiceRows.filter((r) => !r.is_retention_claim), [invoiceRows]);

  const yearOpts = useMemo(() => {
    const s = new Set();
    baseRows.forEach((r) => { if (r.invoice_date) s.add(r.invoice_date.slice(0, 4)); });
    return Array.from(s).sort().reverse();
  }, [baseRows]);
  const projectOpts = useMemo(() => {
    // 149-cw-r140: Sumber project dropdown = SEMUA project visible untuk user
    // (bukan hanya yang punya invoice). Sebelumnya WO baru tanpa invoice tidak
    // muncul di filter dropdown, sehingga user tidak bisa filter untuk WO
    // yang belum di-invoice sama sekali.
    // Kalau `projects` kosong (fallback), pakai baseRows lama supaya tidak break.
    if (projects && projects.length > 0) {
      const s = new Map();
      // Prioritas urut: WO → VO → new_project → internal (sesuai use-case invoice).
      const priority = { working_order: 1, variation_order: 2, new_project: 3, internal: 4 };
      const sorted = [...projects].sort((a, b) => {
        const pa = priority[a.project_type] || 9;
        const pb = priority[b.project_type] || 9;
        if (pa !== pb) return pa - pb;
        return (a.project_name || "").localeCompare(b.project_name || "");
      });
      sorted.forEach((p) => { if (p && p.id) s.set(p.id, p); });
      return Array.from(s.values());
    }
    const s = new Map();
    baseRows.forEach((r) => { if (r.__project) s.set(r.__project.id, r.__project); });
    return Array.from(s.values()).sort((a, b) => (a.project_name || "").localeCompare(b.project_name || ""));
  }, [projects, baseRows]);
  const clientOpts = useMemo(() => {
    const s = new Set();
    baseRows.forEach((r) => { if (r.__project?.client) s.add(r.__project.client); });
    return Array.from(s).sort();
  }, [baseRows]);

  // Induk = proyek tanpa parent (atau parent-nya tidak terlihat di daftar — orphan fallback).
  const parentOpts = useMemo(() => {
    const ids = new Set(projectOpts.map((p) => p.id));
    return projectOpts.filter((p) => !p.parent_project_id || !ids.has(p.parent_project_id));
  }, [projectOpts]);
  // Sub = WO/VO anak dari induk terpilih; kosong (select disabled) sebelum induk dipilih.
  const subOpts = useMemo(() => (
    parentId === "all" ? [] : projectOpts.filter((p) => p.parent_project_id === parentId)
  ), [projectOpts, parentId]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return baseRows.filter((r) => {
      if (year !== "all" && (!r.invoice_date || !r.invoice_date.startsWith(year))) return false;
      if (month !== "all" && (!r.invoice_date || r.invoice_date.slice(5, 7) !== month)) return false;
      if (dateFrom && (!r.invoice_date || r.invoice_date < dateFrom)) return false;
      if (dateTo && (!r.invoice_date || r.invoice_date > dateTo)) return false;
      if (subId !== "all") {
        // Sub Project terpilih → cocokkan persis ke proyek anak (WO/VO) tsb.
        if (r.__project?.id !== subId) return false;
      } else if (parentId !== "all") {
        // Induk saja → sertakan invoice induk + seluruh sub project-nya.
        const head = r.__project?.parent_project_id || r.__project?.id;
        if (head !== parentId) return false;
      }
      if (client !== "all" && r.__project?.client !== client) return false;
      if (status !== "all") {
        const st = getReceiptStatus(r);
        const dueDate = derivedDueDate(r);
        const overdue = dueDate && new Date() > dueDate && st !== "paid";
        const effective = overdue ? "overdue" : st;
        if (effective !== status) return false;
      }
      if (query) {
        const hay = [
          r.__project?.project_name, r.__project?.wo_number,
          r.__project?.variation_order_name, r.__project?.client,
          r.note, r.invoice_date,
        ].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(query)) return false;
      }
      return true;
    }).sort((a, b) => (b.invoice_date || "").localeCompare(a.invoice_date || ""));
  }, [baseRows, q, year, month, parentId, subId, client, status, dateFrom, dateTo]);

  const projById = useMemo(() => new Map((projects || []).map((p) => [p.id, p])), [projects]);

  // 2026-06-11 (user request): grupkan baris invoice per INDUK project.
  // Grup menyimpan total gabungan + status terburuk; baris invoice tetap utuh
  // untuk breakdown via chevron. Urutan grup mengikuti invoice terbaru
  // (filtered sudah terurut invoice_date desc → insertion order Map = recency).
  const groups = useMemo(() => {
    const m = new Map();
    for (const r of filtered) {
      const p = r.__project;
      const headId = p?.parent_project_id || p?.id || "unknown";
      if (!m.has(headId)) {
        m.set(headId, { headId, head: projById.get(headId) || p, rows: [], projectIds: new Set() });
      }
      const g = m.get(headId);
      g.rows.push(r);
      if (p?.id) g.projectIds.add(p.id);
    }
    return Array.from(m.values()).map((g) => {
      const invoiced = g.rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
      const received = g.rows.reduce((s, r) => s + computeTotalReceived(r), 0);
      const remaining = g.rows.reduce((s, r) => s + Math.max(0, computeExpectedNet(r) - computeTotalReceived(r)), 0);
      return {
        ...g, invoiced, received, remaining,
        st: groupStatus(g.rows),
        // Expandable bila >1 invoice atau mencakup >1 project (induk + VO/WO).
        expandable: g.rows.length > 1 || g.projectIds.size > 1,
      };
    });
  }, [filtered, projById]);

  const totalPages = Math.max(1, Math.ceil(groups.length / pageSize));
  const pageGroups = groups.slice((page - 1) * pageSize, page * pageSize);
  const pageInvoiceCount = pageGroups.reduce((s, g) => s + g.rows.length, 0);
  const anyExpandable = groups.some((g) => g.expandable);

  const activeFilterCount = [
    year !== "all", month !== "all", parentId !== "all", subId !== "all", client !== "all", status !== "all",
    !!dateFrom, !!dateTo,
  ].filter(Boolean).length;

  const resetFilters = () => {
    setYear("all"); setMonth("all"); setParentId("all"); setSubId("all"); setClient("all"); setStatus("all");
    setDateFrom(""); setDateTo(""); setPage(1);
  };

  // 149-cw-r138: Sync ulang filter bila `initialProjectId` prop berubah
  // (misal user pindah dari 1 WO ke WO lain lewat URL param baru).
  // 2026-10-14: petakan ke Induk/Sub — WO/VO masuk sebagai Sub Project.
  useEffect(() => {
    if (initialProjectId) {
      const p = (projects || []).find((x) => x.id === initialProjectId)
        || baseRows.find((r) => r.__project?.id === initialProjectId)?.__project;
      if (p?.parent_project_id) { setParentId(p.parent_project_id); setSubId(p.id); }
      else { setParentId(initialProjectId); setSubId("all"); }
      setPage(1);
    }
  }, [initialProjectId, projects, baseRows]);

  if (loading && invoiceRows.length === 0) {
    return <div className="p-8 text-center text-xs italic text-slate-400">Memuat invoice…</div>;
  }

  // 2026-09-24 (user request): hapus invoice langsung dari daftar (backend
  // menolak bila invoice sudah punya catatan pembayaran).
  const deleteInvoice = async (row) => {
    if (!row?.__project) return;
    if (!window.confirm(`Hapus invoice ${shortInvoiceNumber(row)} (${formatIDR(row.amount)})?\nTindakan ini tidak bisa dibatalkan.`)) return;
    try {
      await api.delete(`/active-projects/${row.__project.id}/income/${row.id}`);
      toast.success("Invoice dihapus.");
      onRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menghapus invoice.");
    }
  };

  return (
    <div className="grid grid-cols-1 gap-4" data-testid="fin-invoice-tab">
      <div className="space-y-3 min-w-0">
        {/* Search + Filter + Date range + Export bar (R5 mockup parity) */}
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[240px]">
            <Search size={13} className="absolute left-2.5 top-2.5 text-slate-400" />
            <input
              type="text"
              value={q}
              onChange={(e) => { setQ(e.target.value); setPage(1); }}
              placeholder="Cari nomor invoice, proyek, atau WO…"
              data-testid="fin-invoice-search"
              className="w-full pl-8 pr-2 py-2 text-xs border border-slate-300 focus:outline-none focus:border-[#0B1D3A] rounded-none bg-white"
            />
          </div>
          <button
            type="button"
            onClick={() => setShowFilter((v) => !v)}
            data-testid="fin-invoice-filter-toggle"
            className={`h-9 px-3 border text-xs font-semibold inline-flex items-center gap-1.5 ${activeFilterCount > 0 || showFilter ? "bg-[#0B1D3A] text-white border-[#0B1D3A]" : "border-slate-300 text-slate-700 hover:border-[#0B1D3A] bg-white"}`}
          >
            <Filter size={13} /> Filter {activeFilterCount > 0 && <span className="px-1 py-0.5 bg-white text-[#0B1D3A] text-[9px] font-bold">{activeFilterCount}</span>}
          </button>
          {/* Date range inline (mockup) */}
          <div className="h-9 border border-slate-300 flex items-center bg-white px-2 gap-1.5 text-xs" data-testid="fin-invoice-date-range">
            <Calendar size={13} className="text-slate-500" />
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
              data-testid="fin-invoice-date-from"
              className="border-none focus:outline-none w-[120px] text-slate-700 bg-transparent"
            />
            <span className="text-slate-400">–</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
              data-testid="fin-invoice-date-to"
              className="border-none focus:outline-none w-[120px] text-slate-700 bg-transparent"
            />
            {(dateFrom || dateTo) && (
              <button type="button" onClick={() => { setDateFrom(""); setDateTo(""); }} className="text-slate-400 hover:text-slate-700" data-testid="fin-invoice-date-clear">
                <X size={12} />
              </button>
            )}
          </div>
          <button type="button" className="h-9 px-3 border border-slate-300 text-xs font-semibold text-slate-700 hover:border-[#0B1D3A] bg-white inline-flex items-center gap-1.5" data-testid="fin-invoice-export">
            <Download size={13} /> Ekspor
          </button>
          {/* 149-cw-r140: Tombol "Buat Invoice" primary orange. Solve issue user:
              WO tanpa invoice tidak muncul di filter, dan tidak ada entry point buat invoice. */}
          <div className="relative">
            <button
              type="button"
              data-testid="fin-invoice-create-btn"
              onClick={() => {
                if (initialProjectId) {
                  const scoped = projects.find((p) => p.id === initialProjectId);
                  if (scoped) { setCreateInvoiceDlg({ open: true, project: scoped }); return; }
                }
                setPickerOpen((v) => !v);
              }}
              className="h-9 px-3 bg-[#FF5A1F] hover:bg-[#e04a13] text-white text-xs font-semibold uppercase tracking-wider inline-flex items-center gap-1.5"
              title={initialProjectId ? "Buat Invoice untuk proyek terpilih" : "Pilih proyek & buat invoice baru"}
            >
              <FilePlus2 size={13} /> Buat Invoice
            </button>
            {pickerOpen && !initialProjectId && (
              <div className="absolute right-0 top-[calc(100%+4px)] w-80 bg-white border border-slate-200 shadow-lg z-30" data-testid="fin-invoice-picker-popover">
                <div className="border-b border-slate-200 p-2">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-[#0B1D3A] mb-1.5">Pilih Proyek / WO</div>
                  <div className="relative">
                    <Search size={11} className="absolute left-2 top-2 text-slate-400" />
                    <input
                      type="text"
                      autoFocus
                      value={pickerQ}
                      onChange={(e) => setPickerQ(e.target.value)}
                      placeholder="Cari WO/PO/proyek/klien…"
                      data-testid="fin-invoice-picker-search"
                      className="w-full h-7 pl-7 pr-2 text-xs border border-slate-300 focus:outline-none focus:border-[#0B1D3A] rounded-none"
                    />
                  </div>
                </div>
                <div className="max-h-72 overflow-y-auto">
                  {projectOpts
                    .filter((p) => {
                      const q = pickerQ.trim().toLowerCase();
                      if (!q) return true;
                      return [p.wo_number, p.po_number, p.project_name, p.parent_project_name, p.client, p.variation_order_name]
                        .filter(Boolean).some((s) => String(s).toLowerCase().includes(q));
                    })
                    .slice(0, 80)
                    .map((p) => {
                      const badge = p.project_type === "working_order" ? "WO" : p.project_type === "variation_order" ? "VO" : p.project_type === "internal" ? "KANTOR" : "PROYEK";
                      const badgeCls = p.project_type === "working_order" ? "bg-indigo-100 text-indigo-700 border-indigo-200"
                        : p.project_type === "variation_order" ? "bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200"
                        : p.project_type === "internal" ? "bg-slate-100 text-slate-600 border-slate-300"
                        : "bg-emerald-100 text-emerald-700 border-emerald-200";
                      return (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => { setCreateInvoiceDlg({ open: true, project: p }); setPickerOpen(false); setPickerQ(""); }}
                          data-testid={`fin-invoice-picker-opt-${p.id}`}
                          className="w-full flex items-center gap-2 px-2.5 py-2 text-left text-xs border-b border-slate-100 hover:bg-slate-50"
                        >
                          <span className={`px-1 py-0.5 text-[9px] font-bold uppercase tracking-wider border ${badgeCls} shrink-0`}>{badge}</span>
                          <div className="min-w-0 flex-1">
                            <div className="text-slate-800 font-semibold truncate">
                              {p.project_type === "working_order" ? `${p.wo_number || "—"} · ${p.project_name || p.parent_project_name || "-"}` : projectLabel(p)}
                            </div>
                            {p.client && <div className="text-[10px] text-slate-500 truncate">{p.client}</div>}
                          </div>
                        </button>
                      );
                    })}
                  {projectOpts.length === 0 && (
                    <div className="px-3 py-4 text-center text-[11px] italic text-slate-400">Tidak ada proyek tersedia.</div>
                  )}
                </div>
                <div className="border-t border-slate-200 p-1.5 bg-slate-50 text-center">
                  <button type="button" onClick={() => { setPickerOpen(false); setPickerQ(""); }} className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 hover:text-[#0B1D3A]">Tutup</button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Advanced filter panel */}
        {showFilter && (
          <div className="border border-slate-200 bg-slate-50 p-3 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="fin-invoice-filter-panel">
            <FilterSelect label="Tahun" value={year} onChange={(v) => { setYear(v); setPage(1); }} testid="fin-filter-year"
              options={[{ value: "all", label: "Semua Tahun" }, ...yearOpts.map((y) => ({ value: y, label: y }))]} />
            <FilterSelect label="Bulan" value={month} onChange={(v) => { setMonth(v); setPage(1); }} testid="fin-filter-month"
              options={[{ value: "all", label: "Semua Bulan" },
                ...["01","02","03","04","05","06","07","08","09","10","11","12"].map((m) => ({ value: m, label: ["Jan","Feb","Mar","Apr","Mei","Jun","Jul","Agu","Sep","Okt","Nov","Des"][Number(m)-1] }))]} />
            <FilterSelect label="Induk Project" value={parentId} onChange={(v) => { setParentId(v); setSubId("all"); setPage(1); }} testid="fin-filter-parent-project"
              options={[{ value: "all", label: "Semua Induk Project" }, ...parentOpts.map((p) => ({ value: p.id, label: p.project_name || "-" }))]} />
            <FilterSelect label="Sub Project" value={subId} onChange={(v) => { setSubId(v); setPage(1); }} testid="fin-filter-sub-project"
              disabled={parentId === "all"}
              options={[{ value: "all", label: parentId === "all" ? "Pilih Induk dulu" : "Semua Sub Project" }, ...subOpts.map((p) => ({ value: p.id, label: projectLabel(p) }))]} />
            <FilterSelect label="Client" value={client} onChange={(v) => { setClient(v); setPage(1); }} testid="fin-filter-client"
              options={[{ value: "all", label: "Semua Client" }, ...clientOpts.map((c) => ({ value: c, label: c }))]} />
            <FilterSelect label="Status" value={status} onChange={(v) => { setStatus(v); setPage(1); }} testid="fin-filter-status"
              options={[
                { value: "all", label: "Semua Status" },
                { value: "waiting", label: "Menunggu" },
                { value: "partial", label: "Parsial" },
                { value: "paid", label: "Lunas" },
                { value: "overdue", label: "Terlambat" },
              ]} />
            {activeFilterCount > 0 && (
              <div className="col-span-full flex justify-end">
                <button type="button" onClick={resetFilters} data-testid="fin-filter-reset" className="text-[10px] font-semibold text-[#0B1D3A] hover:underline">
                  Reset Filter
                </button>
              </div>
            )}
          </div>
        )}

        {/* Expand/collapse all controls — muncul hanya bila ada grup multi-invoice. */}
        {anyExpandable && (
          <div className="flex justify-end gap-2" data-testid="fin-inv-expand-controls">
            <button type="button" onClick={() => { setExpandedGroups(new Set(groups.filter((g) => g.expandable).map((g) => g.headId))); }}
              data-testid="fin-inv-expand-all"
              className="text-[10px] uppercase tracking-wider font-semibold text-[#0B1D3A] hover:underline">Buka Semua Rincian</button>
            <button type="button" onClick={() => setExpandedGroups(new Set())}
              data-testid="fin-inv-collapse-all"
              className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 hover:underline">Tutup Semua</button>
          </div>
        )}

        {/* Table — R5 clean mockup styling */}
        <div className="border border-slate-200 overflow-x-auto bg-white">
          <table className="w-full text-xs">
            <thead className="bg-white border-b border-slate-200">
              <tr className="text-left">
                <th className="px-3 py-2.5 font-semibold text-slate-500 text-[10px] uppercase tracking-wider w-[42px]"></th>
                <th className="px-3 py-2.5 font-semibold text-slate-500 text-[10px] uppercase tracking-wider">Nomor Invoice</th>
                <th className="px-3 py-2.5 font-semibold text-slate-500 text-[10px] uppercase tracking-wider">Proyek / WO</th>
                <th className="px-3 py-2.5 font-semibold text-slate-500 text-[10px] uppercase tracking-wider">Tanggal</th>
                <th className="px-3 py-2.5 font-semibold text-slate-500 text-[10px] uppercase tracking-wider text-right">Nilai Tagihan</th>
                <th className="px-3 py-2.5 font-semibold text-slate-500 text-[10px] uppercase tracking-wider text-right">Sudah Dibayar</th>
                <th className="px-3 py-2.5 font-semibold text-slate-500 text-[10px] uppercase tracking-wider text-right">Sisa Piutang</th>
                <th className="px-3 py-2.5 font-semibold text-slate-500 text-[10px] uppercase tracking-wider">Jatuh Tempo</th>
                <th className="px-3 py-2.5 font-semibold text-slate-500 text-[10px] uppercase tracking-wider">Status</th>
                <th className="px-3 py-2.5 font-semibold text-slate-500 text-[10px] uppercase tracking-wider text-right">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {pageGroups.length === 0 && (
                <tr><td colSpan={9} className="text-center py-8 text-slate-400 italic">Tidak ada invoice sesuai filter.</td></tr>
              )}
              {pageGroups.map((g) => {
                const isOpen = expandedGroups.has(g.headId);
                const head = g.head;
                const isCombinedVO = head?.vo_aggregation === "combined";
                const kidTypes = g.rows.map((r) => r.__project?.project_type);
                const kidLabel = kidTypes.every((t) => t === "variation_order") ? "VO" : kidTypes.every((t) => t === "working_order") ? "WO" : "Sub";
                if (!g.expandable) {
                  // Grup 1 invoice → render baris invoice langsung (tanpa header grup).
                  return <InvoiceRow key={g.rows[0].id} r={g.rows[0]} onCatat={(row) => setReceiptCtx({ project: row.__project, entry: row })} onDelete={deleteInvoice} onEdit={(row) => setEditCtx({ project: row.__project, entry: row })} overInfo={overMap[g.rows[0].__project?.id] || null} onOverClassified={onOverClassified} />;
                }
                return (
                  <Fragment key={g.headId}>
                    {/* Baris header grup — total gabungan induk + VO/WO. */}
                    <tr onClick={() => toggleGroup(g.headId)}
                      className="border-b border-slate-200 bg-slate-50/70 hover:bg-slate-100 cursor-pointer transition-colors"
                      data-testid={`fin-inv-group-${g.headId}`}>
                      <td className="px-3 py-2.5">
                        <button type="button"
                          onClick={(e) => { e.stopPropagation(); toggleGroup(g.headId); }}
                          data-testid={`fin-inv-group-expand-${g.headId}`}
                          className="p-0.5 border border-slate-300 text-slate-500 hover:text-[#0B1D3A] hover:border-[#0B1D3A]"
                          title={isOpen ? "Tutup rincian invoice" : "Lihat rincian invoice"}>
                          {isOpen ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                        </button>
                      </td>
                      <td className="px-3 py-2.5 text-[10px] uppercase tracking-wider font-bold text-slate-500 whitespace-nowrap">{g.rows.length} Invoice</td>
                      <td className="px-3 py-2.5 text-slate-800">
                        <div className="font-semibold flex items-center gap-1.5 flex-wrap">
                          <span className="truncate max-w-[220px]" title={head?.project_name}>{head?.project_name || head?.wo_number || "-"}</span>
                          {g.projectIds.size > 1 && (
                            <span className="text-[9px] uppercase tracking-wider font-bold px-1 py-px bg-[#0B1D3A] text-white" data-testid={`fin-inv-group-kids-${g.headId}`}>
                              Main + {g.projectIds.size - 1} {kidLabel}
                            </span>
                          )}
                          {isCombinedVO && <span className="text-[9px] uppercase tracking-wider font-bold px-1 py-px bg-violet-100 text-violet-800 border border-violet-300">VO Gabung</span>}
                        </div>
                        {head?.client && <div className="text-[10px] text-slate-500 truncate max-w-[180px]">{head.client}</div>}
                      </td>
                      <td className="px-3 py-2.5 text-slate-400">—</td>
                      <td className="px-3 py-2.5 tabular-nums text-right font-semibold text-slate-800">{formatIDR(g.invoiced)}</td>
                      <td className={`px-3 py-2.5 tabular-nums text-right ${g.received > 0 ? "text-emerald-700 font-semibold" : "text-slate-400"}`}>{formatIDR(g.received)}</td>
                      <td className={`px-3 py-2.5 tabular-nums text-right font-semibold ${g.remaining > 0 ? "text-rose-700" : "text-slate-400"}`}>{formatIDR(g.remaining)}</td>
                      <td className="px-3 py-2.5 text-slate-400">—</td>
                      <td className="px-3 py-2.5"><StatusBadge status={g.st.status} overdue={g.st.overdue} /></td>
                      <td className="px-3 py-2.5 text-right">
                        <button type="button"
                          onClick={(e) => { e.stopPropagation(); setReceiptCtx({ project: head?.__project || g.rows[0]?.__project || null, entry: null }); }}
                          disabled={!(head?.__project || g.rows[0]?.__project) || g.remaining <= 0.01}
                          data-testid={`fin-catat-group-${g.headId}`}
                          title={g.remaining <= 0.01 ? "Sudah lunas" : "Sinkronkan dari uang masuk e-statement"}
                          className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#0B1D3A] hover:bg-[#122a4d] disabled:opacity-40 text-white text-[10px] uppercase tracking-wider font-semibold">
                          <Plus size={11} /> Catat Pembayaran
                        </button>
                      </td>
                    </tr>
                    {/* Baris invoice — tampil saat grup di-expand. */}
                    {isOpen && g.rows.map((r) => (
                      <InvoiceRow key={r.id} r={r} onCatat={(row) => setReceiptCtx({ project: row.__project, entry: row })} onDelete={deleteInvoice} onEdit={(row) => setEditCtx({ project: row.__project, entry: row })} indent testidPrefix="fin-inv-group-row" overInfo={overMap[r.__project?.id] || null} onOverClassified={onOverClassified} />
                    ))}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination — R5 mockup */}
        <div className="flex items-center justify-between text-xs text-slate-600">
          <div>Menampilkan {pageGroups.length} grup proyek ({pageInvoiceCount} invoice) dari {groups.length} grup · {filtered.length} invoice total</div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              <span>Rows per page</span>
              <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }} className="border border-slate-300 text-xs px-1.5 py-1 bg-white" data-testid="fin-invoice-pagesize">
                {[10, 25, 50, 100, 250].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <PageNumbers page={page} totalPages={totalPages} onPage={setPage} />
          </div>
        </div>
      </div>

      {/* 2026-09-24 (user request): panel "Detail Invoice" dihapus — Catat
          Pembayaran langsung dari tombol di kanan daftar invoice (sinkron
          mutasi e-statement, tanpa input manual). */}
      {receiptCtx && (
        <PaymentReceiptDialog
          open
          onOpenChange={(o) => { if (!o) setReceiptCtx(null); }}
          project={receiptCtx.project}
          entry={receiptCtx.entry}
          onChanged={() => { onRefresh?.(); }}
        />
      )}

      {/* 2026-09-24 (user request): Edit invoice langsung dari daftar. */}
      {editCtx && (
        <EditInvoiceDialog
          project={editCtx.project}
          entry={editCtx.entry}
          onClose={() => setEditCtx(null)}
          onSaved={() => { onRefresh?.(); }}
        />
      )}

      {/* 149-cw-r140: IncomeLedgerDialog — form buat invoice baru. Dipakai oleh tombol
          "Buat Invoice" (scoped WO auto atau via picker popover). */}
      {createInvoiceDlg.open && createInvoiceDlg.project && (
        <IncomeLedgerDialog
          open={createInvoiceDlg.open}
          onOpenChange={(o) => setCreateInvoiceDlg({ open: o, project: o ? createInvoiceDlg.project : null })}
          project={createInvoiceDlg.project}
          onChanged={() => { onRefresh?.(); }}
        />
      )}
    </div>
  );
}

/**
 * InvoiceRow — satu baris invoice (dipakai untuk grup 1-invoice maupun baris
 * rincian di dalam grup yang di-expand). 2026-06-11.
 */
function InvoiceRow({ r, onCatat, onDelete, onEdit, indent = false, testidPrefix = "fin-invoice-row", overInfo = null, onOverClassified = null }) {
  const expected = computeExpectedNet(r);
  const received = computeTotalReceived(r);
  const remaining = Math.max(0, expected - received);
  const st = getReceiptStatus(r);
  const invNumber = shortInvoiceNumber(r);
  const dueDate = derivedDueDate(r);
  const overdue = dueDate && new Date() > dueDate && st !== "paid";
  const ptype = r.__project?.project_type;
  const typeBadge = ptype === "variation_order" ? "VO" : ptype === "working_order" ? "WO" : ptype === "internal" ? "KANTOR" : "MAIN";
  const typeCls = ptype === "variation_order" ? "bg-violet-50 text-violet-700 border-violet-300"
    : ptype === "working_order" ? "bg-emerald-50 text-emerald-700 border-emerald-300"
    : "bg-slate-100 text-slate-600 border-slate-300";
  return (
    <tr className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${indent ? "bg-white" : ""}`}
      data-testid={`${testidPrefix}-${r.id}`}>
      <td className="px-3 py-2.5"></td>
      <td className="px-3 py-2.5 font-mono text-[11px] text-[#0B1D3A] font-semibold">{invNumber}</td>
      <td className="px-3 py-2.5 text-slate-700">
        <div className={`font-semibold flex items-center gap-1.5 ${indent ? "pl-4" : ""}`}>
          {indent && <span className={`shrink-0 text-[9px] uppercase tracking-wider font-bold px-1 py-px border ${typeCls}`}>{typeBadge}</span>}
          <span className="truncate">{projectLabel(r.__project)}</span>
        </div>
        {r.__project?.client && <div className={`text-[10px] text-slate-500 truncate max-w-[180px] ${indent ? "pl-4" : ""}`}>{r.__project.client}</div>}
      </td>
      <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">{r.invoice_date ? fmtDate(r.invoice_date) : "-"}</td>
      <td className="px-3 py-2.5 tabular-nums text-right text-slate-800">{formatIDR(r.amount)}</td>
      <td className={`px-3 py-2.5 tabular-nums text-right ${received > 0 ? "text-emerald-700 font-semibold" : "text-slate-400"}`}>{formatIDR(received)}</td>
      <td className={`px-3 py-2.5 tabular-nums text-right ${remaining > 0 && overdue ? "text-rose-700 font-semibold" : remaining > 0 ? "text-slate-800" : "text-slate-400"}`}>{formatIDR(remaining)}</td>
      <td className={`px-3 py-2.5 whitespace-nowrap ${overdue ? "text-rose-700 font-semibold" : "text-slate-600"}`}>{dueDate ? fmtDate(dueDate.toISOString()) : "-"}</td>
      <td className="px-3 py-2.5">
        <StatusBadge status={st} overdue={overdue} />
        {/* 2026-10-13 (R169): invoice dibatalkan — dikeluarkan dari A.R., tetap tercatat. */}
        {r.is_cancelled && (
          <span className="ml-1 px-1 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-slate-200 text-slate-600 border border-slate-300"
            title={`Dibatalkan${r.cancelled_reason ? `: ${r.cancelled_reason}` : ""}`} data-testid={`fin-inv-cancelled-${r.id}`}>
            Dibatalkan
          </span>
        )}
        {/* 16-CONSISTENT-BADGE-R1: badge klasifikasi selisih (klik → detail; ubah hanya user berwenang). */}
        {overInfo && r.__project?.id && (
          <OverReceivedBadge projectId={r.__project.id} overReceived excessAmount={overInfo.excess_amount}
            classification={overInfo.classification} onClassified={onOverClassified} />
        )}
      </td>
      <td className="px-3 py-2.5 text-right">
        {/* 2026-09-24 (user request): tombol Catat Pembayaran langsung di kanan
            daftar — sinkron dari uang masuk e-statement (tanpa panel Detail). */}
        <div className="inline-flex items-center gap-1">
          <button type="button"
            onClick={(e) => { e.stopPropagation(); onCatat?.(r); }}
            disabled={remaining <= 0.01 || !r.__project}
            data-testid={`fin-catat-${r.id}`}
            title={remaining <= 0.01 ? "Sudah lunas" : "Sinkronkan dari uang masuk e-statement"}
            className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#0B1D3A] hover:bg-[#122a4d] disabled:opacity-40 text-white text-[10px] uppercase tracking-wider font-semibold">
            <Plus size={11} /> Catat Pembayaran
          </button>
          <button type="button"
            onClick={(e) => { e.stopPropagation(); onEdit?.(r); }}
            disabled={!r.__project}
            data-testid={`fin-edit-${r.id}`}
            title="Edit data invoice"
            className="inline-flex items-center justify-center p-1.5 border border-slate-300 text-slate-600 hover:bg-slate-100 disabled:opacity-40">
            <Pencil size={11} />
          </button>
          <button type="button"
            onClick={(e) => { e.stopPropagation(); onDelete?.(r); }}
            disabled={!r.__project}
            data-testid={`fin-del-${r.id}`}
            title="Hapus invoice"
            className="inline-flex items-center justify-center p-1.5 border border-rose-300 text-rose-700 hover:bg-rose-50 disabled:opacity-40">
            <Trash2 size={11} />
          </button>
          {/* 2026-09-25 (Tahap 2 audit): jejak perubahan invoice per baris */}
          <span onClick={(e) => e.stopPropagation()}>
            <AuditLogButton feature_key="invoice" target_id={r.id} title={r.note || r.description || "Invoice"} size="sm" />
          </span>
        </div>
      </td>
    </tr>
  );
}

/**
 * groupStatus — status terburuk dari sekumpulan invoice dalam 1 grup induk.
 * Prioritas: Terlambat > Menunggu > Parsial > Lunas. 2026-06-11.
 */
function groupStatus(rows) {
  let anyOverdue = false, anyWaiting = false, anyPartial = false, allPaid = rows.length > 0;
  for (const r of rows) {
    const st = getReceiptStatus(r);
    const due = derivedDueDate(r);
    const overdue = due && new Date() > due && st !== "paid";
    if (overdue) anyOverdue = true;
    if (st === "waiting") anyWaiting = true;
    if (st === "partial") anyPartial = true;
    if (st !== "paid") allPaid = false;
  }
  if (anyOverdue) return { status: "waiting", overdue: true };
  if (allPaid) return { status: "paid", overdue: false };
  if (anyPartial) return { status: "partial", overdue: false };
  return { status: anyWaiting ? "waiting" : "partial", overdue: false };
}

function PageNumbers({ page, totalPages, onPage }) {  // Render up to 4 numbered page buttons + prev/next chevrons (mockup style)
  const maxBtns = 4;
  const startPage = Math.max(1, Math.min(page - 1, totalPages - maxBtns + 1));
  const pages = [];
  for (let i = 0; i < Math.min(maxBtns, totalPages); i++) {
    pages.push(startPage + i);
  }
  return (
    <div className="flex items-center gap-1" data-testid="fin-invoice-pagination">
      <button type="button" onClick={() => onPage(Math.max(1, page - 1))} disabled={page === 1}
        className="w-7 h-7 border border-slate-300 disabled:opacity-40 hover:border-[#0B1D3A] bg-white text-slate-600" data-testid="fin-invoice-prev">‹</button>
      {pages.map((p) => (
        <button key={p} type="button" onClick={() => onPage(p)}
          className={`w-7 h-7 text-xs font-semibold border ${p === page ? "bg-[#0B1D3A] text-white border-[#0B1D3A]" : "border-slate-300 bg-white text-slate-700 hover:border-[#0B1D3A]"}`}
          data-testid={`fin-invoice-page-${p}`}>{p}</button>
      ))}
      <button type="button" onClick={() => onPage(Math.min(totalPages, page + 1))} disabled={page === totalPages}
        className="w-7 h-7 border border-slate-300 disabled:opacity-40 hover:border-[#0B1D3A] bg-white text-slate-600" data-testid="fin-invoice-next">›</button>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testid, disabled = false }) {
  return (
    <div className="space-y-1">
      <label className={`text-[10px] uppercase tracking-wider font-semibold block ${disabled ? "text-slate-400" : "text-slate-600"}`}>{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} disabled={disabled}
        className="w-full h-8 text-xs border border-slate-300 px-1.5 focus:outline-none focus:border-[#0B1D3A] bg-white disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed">
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

function StatusBadge({ status, overdue }) {
  // R5 mockup: outlined pill, subtle bg, uppercase small text
  if (overdue) return <span className="inline-block px-2.5 py-1 text-[10px] uppercase font-semibold tracking-wider border border-rose-300 bg-rose-50 text-rose-700 rounded-sm" data-testid="fin-status-terlambat">Terlambat</span>;
  if (status === "paid") return <span className="inline-block px-2.5 py-1 text-[10px] uppercase font-semibold tracking-wider border border-emerald-300 bg-emerald-50 text-emerald-700 rounded-sm" data-testid="fin-status-lunas">Lunas</span>;
  if (status === "partial") return <span className="inline-block px-2.5 py-1 text-[10px] uppercase font-semibold tracking-wider border border-amber-300 bg-amber-50 text-amber-700 rounded-sm" data-testid="fin-status-parsial">Parsial</span>;
  return <span className="inline-block px-2.5 py-1 text-[10px] uppercase font-semibold tracking-wider border border-slate-300 bg-slate-50 text-slate-600 rounded-sm" data-testid="fin-status-waiting">Menunggu</span>;
}

function shortInvoiceNumber(entry) {
  const d = entry.invoice_date ? new Date(entry.invoice_date) : null;
  const yr = d ? d.getFullYear() : "----";
  const short = String(entry.id || "").slice(-4).toUpperCase();
  return `INV-${yr}-${short}`;
}

function projectLabel(p) {
  if (!p) return "-";
  if (p.project_type === "working_order") return `${p.wo_number || "WO"} · ${p.project_name || ""}`.trim();
  if (p.project_type === "variation_order") return `VO · ${p.variation_order_name || p.project_name || ""}`.trim();
  return p.project_name || "-";
}

function derivedDueDate(entry) {
  if (!entry.invoice_date) return null;
  try {
    const d = new Date(entry.invoice_date);
    if (isNaN(d.getTime())) return null;
    d.setDate(d.getDate() + 30);
    return d;
  } catch { return null; }
}
